import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CreateRequisitionMasterDialogComponent } from '../../purches-module/requisition/create-requisition-master-dialog/create-requisition-master-dialog.component';
import { PurchaseOrderDialogComponent } from '../../purches-module/purchase-order/purchase-order-dialog/purchase-order-dialog.component';
import { GrnDialogComponent } from '../../purches-module/grn/grn-dialog/grn-dialog.component';
import { CreateQualityCheckDialogComponent } from '../../purches-module/quality-check/create-quality-check-dialog/create-quality-check-dialog.component';
import { CreateVoucherDialogComponent } from '../../create-voucher-dialog/create-voucher-dialog.component';
import { CreateVoucherPostingDialogComponent } from '../../create-voucher-posting-dialog/create-voucher-posting-dialog.component';
import { CreateVoucherPostingPreviewComponent } from '../../create-voucher-posting-preview/create-voucher-posting-preview.component';
import { PurchaseVoucherComponent } from 'app/purchase-voucher/purchase-voucher.component';
import { SalesVoucherComponent } from 'app/sales-voucher/sales-voucher.component';
import { CreditNoteVoucherComponent } from 'app/credit-note-voucher/credit-note-voucher.component';
import { DebitNoteVoucherComponent } from 'app/debit-note-voucher/debit-note-voucher.component';
import { ApiService } from '../../api.service';
import { SalesOrderDialogComponent } from '../../sales-module/sales-order/sales-order-dialog/sales-order-dialog.component';
import { GsnDialogComponent } from '../../sales-module/gsn/gsn-dialog/gsn-dialog.component';
import { SalesPurchaseReturnDialogComponent } from '../../sales-purchase-return-dialog/sales-purchase-return-dialog.component';
import { CreateCompanyItemMasterDialogComponent } from 'app/create-company-item-master-dialog/create-company-item-master-dialog.component';
import { AdminService } from 'app/admin.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UI_DOCUMENT_PANEL_DIALOG_CONFIG } from '../ui/document-panel/document-panel.config';

export interface DocumentViewPayload {
  document_id: number;
  document_type: string;
  [key: string]: any; // Allow additional properties
}

export interface DocumentViewOptions {
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  disableClose?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentViewService {

  constructor(
    private dialog: MatDialog,
    private apiService: ApiService,
    private adminService: AdminService,
    private snackBar: MatSnackBar
  ) { }

  /**
   * Main function to view documents based on document type
   * @param payload - Contains document_id and document_type
   * @param options - Optional dialog configuration
   */
  view(payload: DocumentViewPayload, options: DocumentViewOptions = {}): void {
    const { document_id, document_type } = payload;
    
    if (!document_id || !document_type) {
      console.error('Document ID and Document Type are required');
      return;
    }

    // Validate document type
    if (!this.isDocumentTypeSupported(document_type)) {
      console.error(`Unsupported document type: ${document_type}. Supported types: ${this.getSupportedDocumentTypes().join(', ')}`);
      return;
    }

    // Log the action for debugging
    console.log(`Opening document: ${document_type} with ID: ${document_id}`);

    switch (document_type.toLowerCase()) {
      case 'requisition':
        this.viewRequisition(document_id, options);
        break;
      case 'purchase_order':
      case 'purchaseorder':
        this.viewPurchaseOrder(document_id, options, payload);
        break;
      case 'grn':
        this.viewGRN(document_id, options);
        break;
      case 'quality_check':
      case 'qualitycheck':
        this.viewQualityCheck(document_id, options, payload);
        break;
      case 'voucher':
      case 'creation_voucher':
      case 'creationvoucher':
        this.viewCreationVoucher(document_id, options, payload);
        break;
      case 'voucher_posting':
      case 'voucherposting':
        this.viewVoucherPosting(document_id, options);
        break;
      case 'sales_order':
      case 'salesorder':
        this.viewSalesOrder(document_id, options, payload);
        break;
      case 'gsn':
        this.viewGsn(document_id, options, payload);
        break;
      case 'sales_return':
      case 'salesreturn':
        this.viewSalesPurchaseReturn(document_id, options, payload, 'Sales Return');
        break;
      case 'purchase_return':
      case 'purchasereturn':
        this.viewSalesPurchaseReturn(document_id, options, payload, 'Purchase Return');
        break;
      case 'global_item_master':
      case 'GlobalItemMaster':
        this.viewCompanyItemMaster(document_id);
        break;
      default:
        console.error(`Unsupported document type: ${document_type}`);
        break;
    }
  }

  /**
   * View requisition document
   * @param requisitionId - ID of the requisition
   * @param options - Dialog options
   */
  private viewRequisition(requisitionId: number, options: DocumentViewOptions): void {
    // Fetch requisition data from API
    this.apiService.requisitionForEdit(requisitionId).subscribe(
      (response) => {
        const requisitionData = response[0];
        
        if (requisitionData) {
          const dialogData = this.prepareRequisitionDialogData(requisitionData);
          
          const dialogRef = this.dialog.open(CreateRequisitionMasterDialogComponent, {
            ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
            disableClose: options.disableClose !== false,
            data: dialogData
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              console.log('Requisition dialog closed with result:', result);
            }
          });
        } else {
          console.error(`Requisition with ID ${requisitionId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching requisition data:', error);
      }
    );
  }

  /**
   * View purchase order document
   * @param purchaseOrderId - ID of the purchase order
   * @param options - Dialog options
   */
  private viewPurchaseOrder(purchaseOrderId: number, options: DocumentViewOptions, payload?: DocumentViewPayload): void {
    this.apiService.getPoItem(purchaseOrderId).subscribe(
      (response) => {
        const detail = response?.details?.[0];
        if (!detail) {
          console.error(`Purchase order with ID ${purchaseOrderId} not found`);
          return;
        }
        const rfqIds = detail.quotation_id ? String(detail.quotation_id).split(',').map(Number) : [];
        const requisitionIds = detail.requisition_id ? String(detail.requisition_id).split(',').map(Number) : [];
        const resolvedStatus =
          payload?.approval_status ??
          detail.approval_status ??
          response?.approval_status ??
          detail.purchase_order?.approval_status ??
          null;
        const dialogData = {
          id: purchaseOrderId,
          rfq_ids: rfqIds,
          req_ids: requisitionIds,
          types: 'view',
          is_type: detail.is_type,
          contact_type_id: detail.contact_type_id ?? null,
          approval_status: resolvedStatus,
          profit_cost_id: detail.profit_cost_id,
          short_close: payload?.short_close ?? detail.short_close ?? detail.is_short_close ?? null,
        };

        const dialogRef = this.dialog.open(PurchaseOrderDialogComponent, {
          ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
          disableClose: options.disableClose !== false,
          data: dialogData
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            console.log('Purchase order dialog closed with result:', result);
          }
        });
      },
      (error) => {
        console.error('Error fetching purchase order data:', error);
      }
    );
  }

  /**
   * View GRN document
   * @param grnId - ID of the GRN
   * @param options - Dialog options
   */
  private viewGRN(grnId: number, options: DocumentViewOptions): void {
    this.apiService.showGrnById(grnId).subscribe(
      (response) => {
        const grnData = Array.isArray(response.data) ? response.data[0] : response.data;

        if (grnData) {
          const dialogData = this.prepareGrnDialogData(grnData, grnId);

          const dialogRef = this.dialog.open(GrnDialogComponent, {
            ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
            disableClose: options.disableClose !== false,
            data: dialogData
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              console.log('GRN dialog closed with result:', result);
            }
          });
        } else {
          console.error(`GRN with ID ${grnId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching GRN data:', error);
      }
    );
  }

  /**
   * View quality check document
   * @param qcId - ID of the quality check
   * @param options - Dialog options
   */
  private viewQualityCheck(qcId: number, options: DocumentViewOptions, payload?: DocumentViewPayload): void {
    this.apiService.showQualityCheckById(qcId).subscribe(
      (response) => {
        const qcData = Array.isArray(response.data) ? response.data[0] : response.data;

        if (qcData) {
          const dialogData = this.prepareQualityCheckDialogData(qcData, qcId);
          const resolvedStatus =
            payload?.approval_status ??
            qcData.approval_status ??
            response?.approval_status ??
            null;
          dialogData.approval_status = resolvedStatus;

          const dialogRef = this.dialog.open(CreateQualityCheckDialogComponent, {
            ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
            disableClose: options.disableClose !== false,
            data: dialogData
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              console.log('Quality check dialog closed with result:', result);
            }
          });
        } else {
          console.error(`Quality check with ID ${qcId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching quality check data:', error);
      }
    );
  }

  /**
   * View a creation voucher (Purchase/Sales/Inter Branch/etc.) in the document dialog.
   * Opens Purchase/Sales/Credit/Debit directly to avoid an intermediate dialog close
   * that would wipe the index list.
   */
  private viewCreationVoucher(voucherId: number, options: DocumentViewOptions, payload: DocumentViewPayload = {} as DocumentViewPayload): void {
    this.apiService.getCreationVoucherdetails(voucherId).subscribe(
      (response) => {
        const voucher = Array.isArray(response) ? response[0] : response;
        if (!voucher) {
          console.error(`Creation voucher with ID ${voucherId} not found`);
          return;
        }

        const voucherTypeName = voucher.voucher?.voucher_type || null;
        const designComponents: Record<string, any> = {
          Purchase: PurchaseVoucherComponent,
          Sales: SalesVoucherComponent,
          'Credit Note': CreditNoteVoucherComponent,
          'Debit Note': DebitNoteVoucherComponent,
        };
        const component = designComponents[voucherTypeName] || CreateVoucherPostingDialogComponent;

        const dialogData = {
          id: voucherId,
          type: 'view',
          voucherType: voucher.voucher_id,
          voucherNo: voucher.voucher_no,
          branch_id: voucher.branch_id,
          date: voucher.date,
          upload: voucher.upload,
          narration: voucher.narration,
          debitSides: voucher.creation_voucher_debit,
          creditSides: voucher.creation_voucher_credit,
          showInter: voucherTypeName,
          operationType: 'view',
          approval_status: voucher.approval_status,
          profit_cost_id: voucher.profit_cost_id,
          section_id: voucher.section_id,
          store_id: voucher.store_id,
          layoutType: payload['layoutType'],
          showPostButton: payload['showPostButton'] !== false,
          voucherName: voucherTypeName,
        };

        const dialogRef = this.dialog.open(component, {
          ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
          width: options.width || UI_DOCUMENT_PANEL_DIALOG_CONFIG.width,
          height: options.height || UI_DOCUMENT_PANEL_DIALOG_CONFIG.height,
          maxWidth: options.maxWidth || UI_DOCUMENT_PANEL_DIALOG_CONFIG.maxWidth,
          maxHeight: options.maxHeight || UI_DOCUMENT_PANEL_DIALOG_CONFIG.maxHeight,
          disableClose: options.disableClose !== false,
          data: dialogData,
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (typeof payload['onClosed'] === 'function') {
            payload['onClosed'](result);
          }
          if (result) {
            console.log('Creation voucher dialog closed with result:', result);
          }
        });
      },
      (error) => {
        console.error('Error fetching creation voucher data:', error);
        this.viewVoucher(voucherId, options);
      }
    );
  }

  /**
   * View voucher document
   * @param voucherId - ID of the voucher
   * @param options - Dialog options
   */
  private viewVoucher(voucherId: number, options: DocumentViewOptions): void {
    // Fetch voucher data from API and open posting view (similar to voucher-posting-create component)
    this.apiService.getcreationepostdata(voucherId).subscribe(
      (postData) => {
        console.log("Voucher posting data:", postData);
        
        const dialogRef = this.dialog.open(CreateVoucherPostingPreviewComponent, {
          width: options.width || '850px',
          height: options.height || '550px',
          disableClose: options.disableClose !== false,
          data: {
            view: postData,
            index: 0,
            allData: [postData]
          }
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            console.log('Voucher posting view dialog closed with result:', result);
          }
        });
      },
      (error) => {
        console.error('Error fetching voucher posting data:', error);
        // Fallback to direct voucher dialog if posting data fetch fails
        this.fallbackViewVoucher(voucherId, options);
      }
    );
  }

  /**
   * Fallback method for viewing voucher when posting data fetch fails
   */
  private fallbackViewVoucher(voucherId: number, options: DocumentViewOptions): void {
    // Fetch voucher data from API
    this.apiService.getDataVoucher().subscribe(
      (response) => {
        const voucherData = response.find((item: any) => item.id === voucherId);
        
        if (voucherData) {
          const dialogData = this.prepareVoucherDialogData(voucherData);
          
          const dialogRef = this.dialog.open(CreateVoucherDialogComponent, {
            width: options.width || '600px',
            disableClose: options.disableClose !== false,
            data: dialogData
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              console.log('Voucher dialog closed with result:', result);
            }
          });
        } else {
          console.error(`Voucher with ID ${voucherId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching voucher data:', error);
      }
    );
  }

  /**
   * View voucher posting document
   * @param voucherPostingId - ID of the voucher posting
   * @param options - Dialog options
   */
  private viewVoucherPosting(voucherPostingId: number, options: DocumentViewOptions): void {
    // Fetch voucher posting data from API and open posting view (similar to voucher-posting component)
    this.apiService.getAllpostedvoucher().subscribe(
      (response) => {
        const voucherPostingData = response.posted_voucher?.find((item: any) => item.id === voucherPostingId);
        
        if (voucherPostingData) {
          // Open the posting view dialog with the data
          const dialogRef = this.dialog.open(CreateVoucherPostingPreviewComponent, {
            width: options.width || '850px',
            height: options.height || '550px',
            disableClose: options.disableClose !== false,
            data: {
              view: voucherPostingData,
              index: 0,
              allData: [voucherPostingData]
            }
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              console.log('Voucher posting view dialog closed with result:', result);
            }
          });
        } else {
          console.error(`Voucher posting with ID ${voucherPostingId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching voucher posting data:', error);
        // Fallback to direct voucher posting dialog if fetch fails
        this.fallbackViewVoucherPosting(voucherPostingId, options);
      }
    );
  }

  /**
   * Fallback method for viewing voucher posting when fetch fails
   */
  private fallbackViewVoucherPosting(voucherPostingId: number, options: DocumentViewOptions): void {
    // Fetch voucher posting data from API
    this.apiService.getAllvoucherposting().subscribe(
      (response) => {
        const voucherPostingData = response.data?.find((item: any) => item.id === voucherPostingId);
        
        if (voucherPostingData) {
          const dialogData = this.prepareVoucherPostingDialogData(voucherPostingData);
          
          const dialogRef = this.dialog.open(CreateVoucherPostingDialogComponent, {
            width: options.width || '95%',
            height: options.height || '95%',
            maxWidth: options.maxWidth || '100%',
            disableClose: options.disableClose !== false,
            data: dialogData
          });

                    dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              console.log('Voucher posting dialog closed with result:', result);
            }
          });
        } else {
          console.error(`Voucher posting with ID ${voucherPostingId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching voucher posting data:', error);
      }
    );
  }

  private getCategoryData(detail: any, documentType: string): { categoryId: any, categoryName: any } {
    console.log('detail', detail);
    console.log('documentType', documentType);
    if (documentType === 'Goods') {
      return {
        categoryId: detail.stock_keeping?.profit_cost_category?.category_id,
        categoryName: detail.stock_keeping?.profit_cost_category?.category?.category_name
      };
    } else if (documentType === 'Services'){
      return {
        categoryId: detail.service?.profit_cost_category?.category_id,
        categoryName: detail.service?.profit_cost_category?.category?.category_name
      };
    } else {
        return {
        categoryId: detail.fa_template?.home_entity_category_id,
        categoryName: detail.fa_template?.home_entity_category?.category_name
      };
    }
  }

  // Helper methods to prepare dialog data
  private prepareRequisitionDialogData(requisitionData: any): any {
    return {
      id: requisitionData.id,
      date: requisitionData.date,
      purchase_date: requisitionData.purchase_date,
      req_no: requisitionData.req_no,
      store_id: requisitionData.store_id,
      document_type: requisitionData.document_type,
      branch_id: requisitionData.branch_id,
      remark: requisitionData.remark,
      products: requisitionData.req_details?.map((detail: any) => ({
        id: detail.sku_id,
        sku_id: detail.sku_id,
        specification: detail.specification,
        description: detail.description,
        quantity: detail.qty,
        hsn_no: detail.hsn_no,
        uom: detail.unit?.unit_name,
        unit_id: detail.unit_id,
        start_date: detail.start_date,
        end_date: detail.end_date,
        sku_name: detail.stock_keeping?.sku_name,
        type: detail.item_service_type,
        price: detail.price,
        value: detail.value,
        ledger_name: detail.ledger?.ledger_name,
        ledger_id: detail.ledger?.id,
        item_id: detail.stock_keeping?.item_id,
        item_name: detail.stock_keeping?.item?.item_name,
        branch_name: requisitionData?.branch?.name || null,
        profit_cost_name: requisitionData?.profitcost?.name || null,
        service_name: detail.service?.service_name,
        category_name:this.getCategoryData(detail, requisitionData.document_type).categoryName,
        category_id: this.getCategoryData(detail, requisitionData.document_type).categoryId,
        name: detail.fa_template?.name
      })) || [],
      category_ids: this.extractUniqueCategoryIds(requisitionData.req_details,requisitionData.document_type),
      current_requisition: this.calculateCategoryTotals(requisitionData.req_details,requisitionData.document_type),
      profit_cost_id: requisitionData.profit_cost_id,
      section_id: requisitionData.section_id,
      type: 'View',
      approval_status: requisitionData.approval_status,
    };
  }

  private preparePurchaseOrderDialogData(poData: any): any {
    const rfqIds = poData.quotationcomparision?.map((qc: any) => qc.rfq_id) || [];
    const requisitionIds = poData.requistion?.map((rq: any) => rq.id) || [];
    
    return {
      id: poData.id,
      rfq_ids: rfqIds,
      req_ids: requisitionIds,
      types: 'view',
      is_type: poData.is_type,
      contact_type_id: poData.contact_type_id ?? null,
      approval_status: poData.approval_status,
      profit_cost_id: poData.profit_cost_id,
    };
  }

  private prepareGrnDialogData(grnData: any, grnId?: number): any {
    const poIds = grnData.purchase?.map((po: any) => po.id)
      || (grnData.purchase_order_id ? String(grnData.purchase_order_id).split(',').map(Number) : []);

    return {
      type: 'View',
      id: grnId || grnData.id,
      po_id: poIds,
      approval_status: grnData.approval_status,
    };
  }

  private prepareQualityCheckDialogData(qcData: any, qcId?: number): any {
    return {
      type: 'View',
      id: qcId || qcData.id,
      grn_id: qcData.grn_id,
      date: qcData.date,
      qc_no: qcData.quality_check_no,
      vendor_data: qcData.vendor_id,
      branch_id: qcData.vendor_branch_id,
      profit_cost_id: qcData.profit_cost_id,
      section_id: qcData.section_id,
      approval_status: qcData.approval_status,
      remark: qcData.remark,
    };
  }

  private prepareVoucherDialogData(voucherData: any): any {
    return {
      id: voucherData.id,
      type: "Update",
      name: voucherData.name,
      description: voucherData.description,
      prefix: voucherData.prefix || '',
      cr_no: voucherData.cr_no || 0,
      dr_no: voucherData.dr_no || 0,
      voucher_type: voucherData.voucher_type || '',
    };
  }

  private prepareVoucherPostingDialogData(voucherPostingData: any): any {
    return {
      type: 'Update',
      id: voucherPostingData.id,
      // Add other voucher posting specific fields as needed
    };
  }

  /**
   * View Sales Order document
   */
  private viewSalesOrder(salesOrderId: number, options: DocumentViewOptions, payload?: DocumentViewPayload): void {
    this.apiService.getSoItem(salesOrderId).subscribe(
      (response) => {
        const detail = response?.details?.[0];
        if (!detail) {
          console.error(`Sales order with ID ${salesOrderId} not found`);
          return;
        }
        const resolvedStatus =
          payload?.approval_status ??
          detail.approval_status ??
          response?.approval_status ??
          null;
        const dialogData = {
          id: salesOrderId,
          types: 'view',
          contact_type_id: detail.contact_type_id ?? null,
          approval_status: resolvedStatus,
          profit_cost_id: detail.profit_cost_id,
          short_close: detail.short_close,
        };

        const dialogRef = this.dialog.open(SalesOrderDialogComponent, {
          ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
          disableClose: options.disableClose !== false,
          data: dialogData
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (typeof payload?.['onClosed'] === 'function') {
            payload['onClosed'](result);
          }
          if (result) {
            console.log('Sales order dialog closed with result:', result);
          }
        });
      },
      (error) => {
        console.error('Error fetching sales order data:', error);
        const dialogRef = this.dialog.open(SalesOrderDialogComponent, {
          ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
          disableClose: options.disableClose !== false,
          data: {
            id: salesOrderId,
            types: 'view',
            approval_status: payload?.approval_status ?? null,
          }
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (typeof payload?.['onClosed'] === 'function') {
            payload['onClosed'](result);
          }
        });
      }
    );
  }

  /**
   * View GSN document
   */
  private viewGsn(gsnId: number, options: DocumentViewOptions, payload?: DocumentViewPayload): void {
    this.apiService.showGsnById(gsnId).subscribe(
      (response) => {
        const gsnData = Array.isArray(response.data) ? response.data[0] : response.data;

        if (gsnData) {
          const resolvedStatus =
            payload?.approval_status ??
            gsnData.approval_status ??
            response?.approval_status ??
            null;
          const dialogData = {
            ...this.prepareGsnDialogData(gsnData, gsnId),
            approval_status: resolvedStatus,
          };

          const dialogRef = this.dialog.open(GsnDialogComponent, {
            ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
            disableClose: options.disableClose !== false,
            data: dialogData
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (typeof payload?.['onClosed'] === 'function') {
              payload['onClosed'](result);
            }
          });
        } else {
          console.error(`GSN with ID ${gsnId} not found`);
        }
      },
      (error) => {
        console.error('Error fetching GSN data:', error);
      }
    );
  }

  /**
   * View Sales / Purchase Return document
   */
  private viewSalesPurchaseReturn(
    returnId: number,
    options: DocumentViewOptions,
    payload?: DocumentViewPayload,
    docType?: string,
  ): void {
    this.apiService.getSalesPurchaseReturnById(returnId).subscribe(
      (response) => {
        if (!response?.status || !response?.data?.return) {
          console.error(`Return with ID ${returnId} not found`);
          return;
        }

        const dialogRef = this.dialog.open(SalesPurchaseReturnDialogComponent, {
          ...UI_DOCUMENT_PANEL_DIALOG_CONFIG,
          disableClose: options.disableClose !== false,
          data: {
            type: 'View',
            id: returnId,
            docType: docType || response.data.return.type,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (typeof payload?.['onClosed'] === 'function') {
            payload['onClosed'](result);
          }
        });
      },
      (error) => {
        console.error('Error fetching return data:', error);
      }
    );
  }

  private prepareGsnDialogData(gsnData: any, gsnId?: number): any {
    const soIds = gsnData.sales?.map((so: any) => so.id)
      || (gsnData.sales_order_id ? String(gsnData.sales_order_id).split(',').map(Number) : []);

    return {
      type: 'View',
      id: gsnId || gsnData.id,
      so_id: soIds,
      approval_status: gsnData.approval_status,
      profit_cost_id: gsnData.profit_cost_id,
      section_id: gsnData.section_id,
    };
  }

  private extractUniqueCategoryIds(reqDetails: any[],documentType: string): number[] {
    if (!reqDetails || !Array.isArray(reqDetails)) return [];
    
    const uniqueCategoryIds = [...new Set(reqDetails
      .map(detail => this.getCategoryData(detail, documentType).categoryId)
      .filter(Boolean))];
    return uniqueCategoryIds;
  }

  private calculateCategoryTotals(reqDetails: any[],documentType: string): any[] {
    if (!reqDetails || !Array.isArray(reqDetails)) return [];
    
    const categoryMap = new Map();
    
    reqDetails.forEach(detail => {
      const categoryId = this.getCategoryData(detail, documentType).categoryId;
      const categoryName = this.getCategoryData(detail, documentType).categoryName;
      const amount = detail.value || 0;
      
      if (categoryId && categoryName) {
        if (categoryMap.has(categoryId)) {
          categoryMap.get(categoryId).amount += amount;
        } else {
          categoryMap.set(categoryId, {
            category_id: categoryId,
            category_name: categoryName,
            amount: amount
          });
        }
      }
    });
    
    return Array.from(categoryMap.values());
  }

  /**
   * Get supported document types
   * @returns Array of supported document types
   */
  getSupportedDocumentTypes(): string[] {
    return [
      'requisition',
      'purchase_order',
      'purchaseorder',
      'grn',
      'quality_check',
      'qualitycheck',
      'voucher',
      'creation_voucher',
      'creationvoucher',
      'voucher_posting',
      'voucherposting',
      'sales_order',
      'salesorder',
      'gsn',
      'sales_return',
      'salesreturn',
      'purchase_return',
      'purchasereturn',
      'global_item_master',
      'globalitemmaster'
    ];
  }

  /**
   * Check if a document type is supported
   * @param documentType - Document type to check
   * @returns True if supported, false otherwise
   */
  isDocumentTypeSupported(documentType: string): boolean {
    return this.getSupportedDocumentTypes().includes(documentType.toLowerCase());
  }

  /**
   * Get the display name for a document type
   * @param documentType - Document type to get display name for
   * @returns Display name for the document type
   */
  getDocumentTypeDisplayName(documentType: string): string {
    const displayNames: { [key: string]: string } = {
      'requisition': 'Requisition',
      'purchase_order': 'Purchase Order',
      'purchaseorder': 'Purchase Order',
      'grn': 'Goods Receipt Note (GRN)',
      'quality_check': 'Quality Check',
      'qualitycheck': 'Quality Check',
      'voucher': 'Voucher',
      'creation_voucher': 'Voucher',
      'creationvoucher': 'Voucher',
      'voucher_posting': 'Voucher Posting',
      'voucherposting': 'Voucher Posting',
      'sales_order': 'Sales Order',
      'salesorder': 'Sales Order',
      'gsn': 'GSN',
      'sales_return': 'Sales Return',
      'salesreturn': 'Sales Return',
      'purchase_return': 'Purchase Return',
      'purchasereturn': 'Purchase Return',
      'globalitemmaster': 'Global Item Master',
      'global_item_master': 'Global Item Master'
    };
    
    return displayNames[documentType.toLowerCase()] || documentType;
  }

  private viewCompanyItemMaster(companyItemMasterId: any): void {
    // Fetch company item master data from API

    this.adminService.getGlobalItem().subscribe(
      (response) => {
        console.log('GlobalItemMaster response:', response);
        const itemView = response.data?.find((item: any) => item.id === companyItemMasterId);
        
        if (itemView) {
          const dialogRef = this.dialog.open(CreateCompanyItemMasterDialogComponent, {
            width: '600px',
            disableClose: true,
            data: {
              id: companyItemMasterId,
              type: 'View',
              item_name: itemView.item_name,
              description: itemView.description,
              unit_id: itemView.unit?.[0]?.unit_id,
              hsn_id: itemView.hsn_id
            },
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              console.log('Item view:', result);
            } else {
              console.log('Modal closed without updating data');
            }
          });
        } else {
          console.error(`GlobalItemMaster with ID ${companyItemMasterId} not found`);
          this.snackBar.open(`Item with ID ${companyItemMasterId} not found`, 'Close', {
            duration: 3000
          });
        }
      },
      (error) => {
        console.error('Error fetching GlobalItemMaster data:', error);
        this.snackBar.open('Error loading item data', 'Close', {
          duration: 3000
        });
      }
    );
  }
} 